import React, { useState } from 'react';
import NewsletterSignup from '../components/NewsletterSignup';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '' // honeypot field
  });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('');

    // Honeypot check - if filled, it's a bot
    if (formData.website) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://6mmweyheo6.execute-api.us-east-1.amazonaws.com/prod/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('Thanks for reaching out! I\'ll get back to you soon.');
        setFormData({ name: '', email: '', message: '', website: '' });
      } else {
        setStatus(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-page">
      {/* Newsletter Section (Hero) */}
      <section id="newsletter" className="newsletter-hero-section" style={{ backgroundImage: 'url(/images/sunset.jpg)', backgroundPosition: 'center 50%' }}>
        <div className="container">
          <div className="newsletter-hero-content">
            <h1>Brainwaves by Kaleb Pattawi</h1>
            <p className="newsletter-tagline">
              A weekly-ish note about what I'm thinking about.
            </p>
            <p className="newsletter-description">
              Sometimes it's about fitness or training. Sometimes work, money, or long-term planning. 
              Sometimes it's a story from travel or a question I can't stop thinking about. The common 
              thread is curiosity and experimentation — writing to clarify my own thinking and hopefully 
              spark something useful for you.
            </p>
            <p className="newsletter-cta">
              If you subscribe, you'll get honest reflections, not recycled advice. And if you ever reply, I read them!
            </p>
            <NewsletterSignup />
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2>About</h2>
          <div className="about-content">
            <p>
              I'm Kaleb. I'm an engineer by trade, but most of my energy goes into learning how to spend 
              my time better — physically, mentally, and creatively.
            </p>
            
            <p>
              I care a lot about staying active and capable. I train regularly, play beach volleyball, 
              and like pushing myself to learn physical skills, whether that's parkour, building surfboards, 
              or just seeing what my body can do when I'm consistent. I also enjoy building things with code, 
              thinking in systems, and using data to make better decisions.
            </p>
            
            <p>
              Travel has been another big teacher for me. Seeing how other people live — and how much history 
              and perspective exists outside my own bubble — has shaped how I think about work, ambition, and 
              what actually matters. I'm also slowly learning Japanese.
            </p>
            
            <p>
              This site and newsletter exist as a place to think out loud. I write about what I'm experimenting 
              with: fitness routines, career decisions, money habits, travel experiences, and the mental models 
              I use to make sense of it all. It's a record of trying to live deliberately and learning as I go.
            </p>
          </div>
          
          <div className="photo-collage">
            <div className="collage-container">
              <div className="photo photo-2">
                <img src="/images/IMG_4709.jpeg" alt="Beach Volleyball" />
              </div>
              <div className="photo photo-3">
                <img src="/images/IMG_7938.jpeg" alt="Adventure" />
              </div>
              <div className="photo photo-4">
                <img src="/images/IMG_9113.jpeg" alt="Parkour" />
              </div>
              <div className="photo photo-5">
                <img src="/images/IMG_8851.jpeg" alt="Travel" />
              </div>
              <div className="photo photo-6">
                <img src="/images/IMG_4744.jpeg" alt="Engineering" />
              </div>
              <div className="photo photo-7">
                <img src="/images/IMG_9823.JPG" alt="Learning" />
              </div>
              <div className="photo photo-8">
                <img src="/images/IMG_8044.jpeg" alt="Adventure" />
              </div>
              <div className="photo photo-9">
                <img src="/images/IMG_9124.JPG" alt="Activity" />
              </div>
              <div className="photo photo-10">
                <img src="/images/IMG-20251206-WA0018.JPG" alt="Life" />
              </div>
              <div className="photo photo-11">
                <img src="/images/PB300128.JPG" alt="Experience" />
              </div>
              <div className="photo photo-12">
                <img src="/images/thai_ladybois (57 of 93).JPG" alt="Travel Adventure" />
              </div>
              <div className="photo photo-13">
                <img src="/images/thai_ladybois (62 of 93).jpg" alt="Cultural Experience" />
              </div>
              <div className="photo photo-14">
                <img src="/images/IMG_0245.jpeg" alt="Adventure" />
              </div>
              <div className="photo photo-15">
                <img src="/images/IMG_3847.jpeg" alt="Experience" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <h2>Get in Touch</h2>
          
          <div className="contact-content">
            <div className="contact-intro">
              <p>
                I love connecting with people! Whether you have questions about something 
                I've written, want to share your own experiences, or just want to say hi, 
                I'd love to hear from you.
              </p>
              <p>
                I'm always interested in hearing about new hobbies, travel recommendations, 
                engineering challenges, or anything else that's got you excited lately.
              </p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              {/* Honeypot field - hidden from users, bots will fill it */}
              <div className="honeypot">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {status && (
                <p className={status.includes('Thanks') ? 'success-message' : 'error-message'}>
                  {status}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
      
      {/* Blog Section */}
      <section id="blog" className="blog-section">
        <div className="container">
          <h2>Previous Newsletters</h2>
          <p className="blog-intro">
            Past editions of Brainwaves. Each one is a snapshot of what I was thinking about at the time.
          </p>

          <div className="posts-grid">
            <p className="coming-soon">Newsletter archive coming soon! Subscribe above to get the latest editions.</p>
            {/* This will be populated with newsletter cards */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;